import { matchSecondaryUnit } from "@redotech/redo-address-common/secondary-unit";

export interface ParsedAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Attempts to parse a full address string into components locally.
 * Returns the parsed address if successful, or null if parsing failed
 * and the address should be sent to the LLM.
 */
export function tryParseAddressLocally(
  fullAddress: string,
): ParsedAddress | null {
  const trimmed = fullAddress.trim();
  if (!trimmed) {
    return {
      street1: "",
      street2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    };
  }

  // Try US address format first (most common)
  const usResult = tryParseUsAddress(trimmed);
  if (usResult && isSuccessfulParse(usResult)) {
    return usResult;
  }

  // Try Canadian address format
  const caResult = tryParseCanadianAddress(trimmed);
  if (caResult && isSuccessfulParse(caResult)) {
    return caResult;
  }

  // Try UK address format
  const ukResult = tryParseUkAddress(trimmed);
  if (ukResult && isSuccessfulParse(ukResult)) {
    return ukResult;
  }

  // Try generic comma-separated format
  const genericResult = tryParseGenericAddress(trimmed);
  if (genericResult && isSuccessfulParse(genericResult)) {
    return genericResult;
  }

  // Could not parse locally
  return null;
}

/**
 * Check if a parse result has enough components to be considered successful.
 * We require at least street1 and one of (city, state, postalCode).
 */
function isSuccessfulParse(result: ParsedAddress): boolean {
  const hasStreet = result.street1.trim().length > 0;
  const hasLocation =
    result.city.trim().length > 0 ||
    result.state.trim().length > 0 ||
    result.postalCode.trim().length > 0;

  return hasStreet && hasLocation;
}

// US state abbreviations and full names
const US_STATES: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "district of columbia": "DC",
  "puerto rico": "PR",
};

const US_STATE_ABBREVS = new Set(Object.values(US_STATES));

// Canadian province abbreviations
const CA_PROVINCES: Record<string, string> = {
  alberta: "AB",
  "british columbia": "BC",
  manitoba: "MB",
  "new brunswick": "NB",
  "newfoundland and labrador": "NL",
  newfoundland: "NL",
  "northwest territories": "NT",
  "nova scotia": "NS",
  nunavut: "NU",
  ontario: "ON",
  "prince edward island": "PE",
  quebec: "QC",
  saskatchewan: "SK",
  yukon: "YT",
};

const CA_PROVINCE_ABBREVS = new Set(Object.values(CA_PROVINCES));

// Common secondary address unit indicators

/**
 * Tries to parse a US-style address.
 * Format: "123 Main St, City, ST 12345" or "123 Main St, Apt 4, City, ST 12345-6789"
 * Works right-to-left to avoid matching PO Box numbers as ZIP codes.
 */
function tryParseUsAddress(address: string): ParsedAddress | null {
  let remainingAddress = address.trim();
  let country = "";
  let postalCode = "";
  let state = "";

  // Step 1: Extract and remove country if at end
  const countryPatterns = [
    /,?\s*(united states of america)$/i,
    /,?\s*(united states|usa|u\.s\.a\.?|u\.s\.?)$/i,
    /,?\s*\bus\b$/i,
  ];

  for (const pattern of countryPatterns) {
    const countryMatch = remainingAddress.match(pattern);
    if (countryMatch) {
      country = "US";
      remainingAddress = remainingAddress
        .slice(0, countryMatch.index)
        .trim()
        .replace(/,\s*$/, "");
      break;
    }
  }

  // Step 2: Look for ZIP code at the END of the address (after removing country)
  // Pattern: 5 digits optionally followed by -4 digits, at the end
  const zipAtEndPattern = /,?\s*(\d{5})(?:-(\d{4}))?\s*$/;
  const zipMatch = remainingAddress.match(zipAtEndPattern);

  if (zipMatch && zipMatch[1]) {
    postalCode = zipMatch[2] ? `${zipMatch[1]}-${zipMatch[2]}` : zipMatch[1];
    remainingAddress = remainingAddress
      .slice(0, zipMatch.index)
      .trim()
      .replace(/,\s*$/, "");
  }

  // Step 3: Look for state abbreviation at the end (after removing ZIP)
  // Pattern: comma followed by 2-letter state code at end (must have comma to ensure it's separate)
  // OR the entire remaining string is just "City, ST" format
  const stateWithCommaPattern = /,\s*([A-Z]{2})\s*$/i;
  const stateMatch = remainingAddress.match(stateWithCommaPattern);

  if (stateMatch && stateMatch[1]) {
    const stateAbbr = stateMatch[1].toUpperCase();
    if (US_STATE_ABBREVS.has(stateAbbr)) {
      state = stateAbbr;
      country = country || "US";
      remainingAddress = remainingAddress
        .slice(0, stateMatch.index)
        .trim()
        .replace(/,\s*$/, "");
    }
  }

  // Try full state name if no abbreviation found
  if (!state) {
    for (const [fullName, abbr] of Object.entries(US_STATES)) {
      const statePattern = new RegExp(`,?\\s*${fullName}\\s*$`, "i");
      const fullStateMatch = remainingAddress.match(statePattern);
      if (fullStateMatch) {
        state = abbr;
        country = country || "US";
        remainingAddress = remainingAddress
          .slice(0, fullStateMatch.index)
          .trim()
          .replace(/,\s*$/, "");
        break;
      }
    }
  }

  // If we didn't find a ZIP code OR state, this probably isn't a US address
  if (!postalCode && !state) {
    return null;
  }

  // Step 4: Extract city (last comma-separated part of remaining address)
  const parts = remainingAddress
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  // If there's only one part and we have a state, that means the address is missing
  // commas between street and city (e.g., "55 Cottonwood Dr Columbus" for "Columbus, OH")
  // These are ambiguous and should be sent to the LLM for better parsing
  if (parts.length === 1 && state) {
    return null;
  }

  const city = parts.length > 1 ? parts.pop()! : "";

  // Step 5: The rest is street address
  const streetParts = parts.join(", ");

  // Try to separate street1 and street2
  const { street1, street2 } = extractStreetParts(streetParts);

  return {
    street1: cleanField(street1),
    street2: cleanField(street2),
    city: cleanField(city),
    state: cleanField(state),
    postalCode: cleanField(postalCode),
    country: cleanField(country),
  };
}

/**
 * Tries to parse a Canadian address.
 * Format: "123 Main St, City, AB A1A 1A1"
 */
function tryParseCanadianAddress(address: string): ParsedAddress | null {
  // Canadian postal code pattern: A1A 1A1 or A1A1A1
  const postalPattern = /\b([A-Z]\d[A-Z])\s*(\d[A-Z]\d)\b/i;

  const postalMatch = address.match(postalPattern);
  if (!postalMatch) {
    return null;
  }

  const postalCode = `${postalMatch[1]!.toUpperCase()} ${postalMatch[2]!.toUpperCase()}`;
  let remainingAddress = address.replace(postalPattern, "").trim();
  let country = "";
  let state = "";

  // Extract and remove country if at end
  const countryPatterns = [/,?\s*(canada|ca)$/i];

  for (const pattern of countryPatterns) {
    const countryMatch = remainingAddress.match(pattern);
    if (countryMatch) {
      country = "CA";
      remainingAddress = remainingAddress.replace(pattern, "").trim();
      break;
    }
  }

  // Extract province abbreviation
  const provAbbrMatch = remainingAddress.match(/,\s*([A-Z]{2})\s*$/i);
  if (provAbbrMatch && provAbbrMatch[1]) {
    const provAbbr = provAbbrMatch[1].toUpperCase();
    if (CA_PROVINCE_ABBREVS.has(provAbbr)) {
      state = provAbbr;
      remainingAddress = remainingAddress.replace(/,\s*[A-Z]{2}\s*$/i, "");
      country = country || "CA";
    }
  }

  // Try full province name if no abbreviation found
  if (!state) {
    for (const [fullName, abbr] of Object.entries(CA_PROVINCES)) {
      const provPattern = new RegExp(`,\\s*${fullName}\\s*$`, "i");
      if (provPattern.test(remainingAddress)) {
        state = abbr;
        remainingAddress = remainingAddress.replace(provPattern, "");
        country = country || "CA";
        break;
      }
    }
  }

  // Extract city
  const parts = remainingAddress.split(",").map((p) => p.trim());
  const city = parts.length > 1 ? parts.pop()! : "";

  // The rest is street address
  const streetParts = parts.join(", ");
  const { street1, street2 } = extractStreetParts(streetParts);

  return {
    street1: cleanField(street1),
    street2: cleanField(street2),
    city: cleanField(city),
    state: cleanField(state),
    postalCode: cleanField(postalCode),
    country: cleanField(country),
  };
}

/**
 * Tries to parse a UK address.
 * Format: "123 Main St, City, AB1 2CD" or "123 Main St, City AB1 2CD"
 */
function tryParseUkAddress(address: string): ParsedAddress | null {
  // UK postcode patterns (simplified)
  // Format: A9 9AA, A99 9AA, A9A 9AA, AA9 9AA, AA99 9AA, AA9A 9AA
  const postcodePattern =
    /\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s*(\d[A-Z]{2})\b(?:\s*,?\s*(uk|united kingdom|england|scotland|wales|northern ireland)?)?$/i;

  const postcodeMatch = address.match(postcodePattern);
  if (!postcodeMatch) {
    return null;
  }

  const postalCode = `${postcodeMatch[1]!.toUpperCase()} ${postcodeMatch[2]!.toUpperCase()}`;
  let country = postcodeMatch[3] ? "UK" : "";
  let remainingAddress = address
    .replace(postcodePattern, "")
    .trim()
    .replace(/,\s*$/, "");

  // Extract and remove country if at end (if not already captured)
  if (!country) {
    const countryPatterns = [
      /,?\s*(uk|united kingdom|britain|great britain)$/i,
    ];
    for (const pattern of countryPatterns) {
      const countryMatch = remainingAddress.match(pattern);
      if (countryMatch) {
        country = "UK";
        remainingAddress = remainingAddress.replace(pattern, "").trim();
        break;
      }
    }
  }

  // For UK, we don't have states/provinces in the same way
  // The last comma-separated part before the postcode is typically the city/town
  const parts = remainingAddress.split(",").map((p) => p.trim());
  const city = parts.length > 1 ? parts.pop()! : "";

  const streetParts = parts.join(", ");
  const { street1, street2 } = extractStreetParts(streetParts);

  return {
    street1: cleanField(street1),
    street2: cleanField(street2),
    city: cleanField(city),
    state: "",
    postalCode: cleanField(postalCode),
    country: cleanField(country),
  };
}

/**
 * Tries to parse a generic comma-separated address.
 * This is a fallback for addresses that don't match specific country patterns.
 */
function tryParseGenericAddress(address: string): ParsedAddress | null {
  const parts = address.split(",").map((p) => p.trim());

  if (parts.length < 2) {
    return null;
  }

  // Try to identify components from the end
  let country = "";
  let postalCode = "";
  let state = "";
  let city = "";
  let streetEndIndex = parts.length;

  // Check if last part is a country
  const lastPart = parts[parts.length - 1];
  if (lastPart && isLikelyCountry(lastPart)) {
    country = normalizeCountry(lastPart);
    streetEndIndex--;
  }

  // Check for postal code in remaining last parts
  for (let i = streetEndIndex - 1; i >= Math.max(0, streetEndIndex - 2); i--) {
    const part = parts[i];
    if (!part) {
      continue;
    }
    const postalMatch = extractPostalCode(part);
    if (postalMatch) {
      postalCode = postalMatch.postalCode;
      const remaining = postalMatch.remaining.trim();
      if (remaining) {
        // The remaining part might be "City, ST" or just "City" or "ST"
        const stateMatch = remaining.match(/^([A-Z]{2})$/i);
        if (
          stateMatch &&
          stateMatch[1] &&
          (US_STATE_ABBREVS.has(stateMatch[1].toUpperCase()) ||
            CA_PROVINCE_ABBREVS.has(stateMatch[1].toUpperCase()))
        ) {
          state = stateMatch[1].toUpperCase();
        } else {
          city = remaining;
        }
      }
      streetEndIndex = i;
      break;
    }
  }

  // Check for state in remaining parts
  if (!state && streetEndIndex > 1) {
    const potentialState = parts[streetEndIndex - 1];
    if (potentialState) {
      const stateMatch = potentialState.match(/^([A-Z]{2})$/i);
      if (
        stateMatch &&
        stateMatch[1] &&
        (US_STATE_ABBREVS.has(stateMatch[1].toUpperCase()) ||
          CA_PROVINCE_ABBREVS.has(stateMatch[1].toUpperCase()))
      ) {
        state = stateMatch[1].toUpperCase();
        streetEndIndex--;
      }
    }
  }

  // City is typically the part before state/postal
  if (!city && streetEndIndex > 1) {
    city = parts[streetEndIndex - 1] ?? "";
    streetEndIndex--;
  }

  // If we have a state but no city, the address is likely missing commas
  // between street and city - send to LLM for better parsing
  if (state && !city) {
    return null;
  }

  // Everything else is street address
  const streetParts = parts.slice(0, streetEndIndex).join(", ");
  const { street1, street2 } = extractStreetParts(streetParts);

  return {
    street1: cleanField(street1),
    street2: cleanField(street2),
    city: cleanField(city),
    state: cleanField(state),
    postalCode: cleanField(postalCode),
    country: cleanField(country),
  };
}

/**
 * Attempts to separate street1 and street2 from a combined street string.
 */
function extractStreetParts(street: string): {
  street1: string;
  street2: string;
} {
  const trimmed = street.trim();

  // Check for secondary unit indicators
  const unitMatch = matchSecondaryUnit(trimmed);
  if (unitMatch) {
    const unitIndex = trimmed.indexOf(unitMatch);
    // Check if unit info is at the beginning or after a comma
    const beforeUnit = trimmed.slice(0, unitIndex).trim();
    const unitAndAfter = trimmed.slice(unitIndex).trim();

    if (beforeUnit.endsWith(",")) {
      return { street1: beforeUnit.slice(0, -1).trim(), street2: unitAndAfter };
    } else if (beforeUnit) {
      // Unit info might be in the middle - keep street1 before it
      return { street1: beforeUnit, street2: unitAndAfter };
    }
  }

  // If address has multiple comma-separated parts, first is street1, rest could be street2
  const commaParts = trimmed.split(",").map((p) => p.trim());
  if (commaParts.length >= 2) {
    return {
      street1: commaParts[0] ?? "",
      street2: commaParts.slice(1).join(", "),
    };
  }

  return { street1: trimmed, street2: "" };
}

/**
 * Checks if a string is likely a country name.
 */
function isLikelyCountry(str: string): boolean {
  const lower = str.toLowerCase().trim();
  const countries = [
    "united states",
    "usa",
    "us",
    "u.s.a.",
    "u.s.",
    "united states of america",
    "canada",
    "ca",
    "united kingdom",
    "uk",
    "britain",
    "great britain",
    "england",
    "scotland",
    "wales",
    "australia",
    "au",
    "new zealand",
    "nz",
    "germany",
    "de",
    "france",
    "fr",
    "spain",
    "es",
    "italy",
    "it",
    "mexico",
    "mx",
    "japan",
    "jp",
    "china",
    "cn",
    "india",
    "in",
    "brazil",
    "br",
  ];
  return countries.includes(lower);
}

/**
 * Normalizes a country name to its ISO code.
 */
function normalizeCountry(str: string): string {
  const lower = str.toLowerCase().trim();
  const countryMap: Record<string, string> = {
    "united states": "US",
    usa: "US",
    us: "US",
    "u.s.a.": "US",
    "u.s.": "US",
    "united states of america": "US",
    canada: "CA",
    ca: "CA",
    "united kingdom": "UK",
    uk: "UK",
    britain: "UK",
    "great britain": "UK",
    england: "UK",
    scotland: "UK",
    wales: "UK",
    australia: "AU",
    au: "AU",
    "new zealand": "NZ",
    nz: "NZ",
    germany: "DE",
    de: "DE",
    france: "FR",
    fr: "FR",
    spain: "ES",
    es: "ES",
    italy: "IT",
    it: "IT",
    mexico: "MX",
    mx: "MX",
    japan: "JP",
    jp: "JP",
    china: "CN",
    cn: "CN",
    india: "IN",
    in: "IN",
    brazil: "BR",
    br: "BR",
  };
  return countryMap[lower] || str;
}

/**
 * Tries to extract a postal code from a string and returns the remaining part.
 */
function extractPostalCode(
  str: string,
): { postalCode: string; remaining: string } | null {
  const patterns = [
    // US ZIP: 12345 or 12345-6789
    {
      regex: /\b(\d{5})(?:-(\d{4}))?\b/,
      format: (m: RegExpMatchArray) => (m[2] ? `${m[1]}-${m[2]}` : m[1]!),
    },
    // Canadian: A1A 1A1
    {
      regex: /\b([A-Z]\d[A-Z])\s*(\d[A-Z]\d)\b/i,
      format: (m: RegExpMatchArray) =>
        `${m[1]!.toUpperCase()} ${m[2]!.toUpperCase()}`,
    },
    // UK: Various formats
    {
      regex: /\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s*(\d[A-Z]{2})\b/i,
      format: (m: RegExpMatchArray) =>
        `${m[1]!.toUpperCase()} ${m[2]!.toUpperCase()}`,
    },
  ];

  for (const { regex, format } of patterns) {
    const match = str.match(regex);
    if (match) {
      return {
        postalCode: format(match),
        remaining: str.replace(regex, "").trim().replace(/,\s*$/, ""),
      };
    }
  }

  return null;
}

/**
 * Cleans a field value by trimming whitespace and removing trailing commas.
 */
function cleanField(value: string): string {
  return value
    .trim()
    .replace(/^,+|,+$/g, "")
    .trim();
}
